'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/libs/supabase/client';
import ButtonSignin from '@/components/ButtonSignin';
import { User } from '@supabase/supabase-js';

interface FeatureRequest {
  id: string;
  user_id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  created_at: string;
  updated_at: string;
  admin_comment: string | null;
  upvote_count: number;
  user_has_upvoted: boolean;
  user_name: string;
  user_email: string;
}

export default function FeatureRequestsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [newRequest, setNewRequest] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user, statusFilter, sortBy, searchQuery]);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();
        setIsAdmin(profile?.is_admin === true);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error checking auth:', error);
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        sortBy: sortBy === 'upvotes' ? 'upvotes' : sortBy,
        order: 'desc'
      });

      const response = await fetch(`/api/feature-requests?${params}`);
      const { data, error } = await response.json();

      if (error) {
        console.error('Error fetching requests:', error);
      } else {
        // Filter by search query on the client side
        let filteredData = data || [];
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filteredData = filteredData.filter((request: FeatureRequest) => 
            request.title.toLowerCase().includes(query) || 
            request.description.toLowerCase().includes(query)
          );
        }
        setRequests(filteredData);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRequest.title || !newRequest.description) return;

    setSubmitting(true);
    try {
      const response = await fetch('/api/feature-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRequest),
      });

      if (response.ok) {
        setNewRequest({ title: '', description: '' });
        setShowNewRequestForm(false);
        fetchRequests();
      } else {
        const { error } = await response.json();
        console.error('Error submitting request:', error);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (requestId: string, hasUpvoted: boolean) => {
    try {
      const response = await fetch(`/api/feature-requests/${requestId}/upvote`, {
        method: hasUpvoted ? 'DELETE' : 'POST',
      });

      if (response.ok) {
        fetchRequests();
      }
    } catch (error) {
      console.error('Error toggling upvote:', error);
    }
  };

  const handleSaveComment = async (requestId: string) => {
    setSavingComment(true);
    try {
      const response = await fetch(`/api/feature-requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_comment: commentText }),
      });
      if (response.ok) {
        setEditingComment(null);
        setCommentText('');
        fetchRequests();
      }
    } catch (error) {
      console.error('Error saving comment:', error);
    } finally {
      setSavingComment(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'in_progress':
        return 'badge-warning';
      case 'rejected':
        return 'badge-error';
      default:
        return 'badge-neutral';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card w-full max-w-md bg-base-200">
          <div className="card-body text-center">
            <h2 className="card-title justify-center text-2xl mb-4">Sign in Required</h2>
            <p className="mb-6">Please sign in to view and submit feature requests</p>
            <ButtonSignin />
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen p-8 pb-24">
      <section className="max-w-4xl mx-auto space-y-8">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-extrabold">Feature Requests</h1>
            <p className="text-base-content/70">
              Help me make ChangeLyric better by submitting and voting on features
            </p>
          </div>
          <a href="/v2" className="btn btn-sm btn-outline">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Dashboard
          </a>
        </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={() => setShowNewRequestForm(!showNewRequestForm)}
          className="btn btn-primary"
        >
          {showNewRequestForm ? 'Cancel' : 'New Request'}
        </button>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search requests..."
          className="input input-bordered flex-1 min-w-[200px]"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="select select-bordered"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="select select-bordered"
        >
          <option value="created_at">Newest</option>
          <option value="upvotes">Most Upvoted</option>
        </select>
      </div>

      {showNewRequestForm && (
        <div className="card bg-base-200 mb-6">
          <div className="card-body">
            <h3 className="card-title">Submit New Feature Request</h3>
            <form onSubmit={handleSubmitRequest}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Title</span>
                </label>
                <input
                  type="text"
                  value={newRequest.title}
                  onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                  className="input input-bordered"
                  placeholder="Brief title for your feature request"
                  required
                />
              </div>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  value={newRequest.description}
                  onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                  className="textarea textarea-bordered h-32"
                  placeholder="Describe your feature request in detail..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary"
              >
                {submitting ? <span className="loading loading-spinner"></span> : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base-content/70">No feature requests yet. Be the first to submit one!</p>
          </div>
        ) : (
          requests.map((request) => (
            <div key={request.id} className="card bg-base-200">
              <div className="card-body">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">{request.title}</h3>
                      <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                        {request.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-base-content/70 mb-4 whitespace-pre-wrap">
                      {request.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-base-content/60">
                      <span>by {request.user_name || 'Anonymous'}</span>
                      <span>•</span>
                      <span>{new Date(request.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Admin comment display */}
                    {request.admin_comment && editingComment !== request.id && (
                      <div className="mt-3 p-3 bg-base-300 rounded-lg border-l-4 border-primary">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-primary uppercase">Admin Response</span>
                          {isAdmin && (
                            <button
                              onClick={() => {
                                setEditingComment(request.id);
                                setCommentText(request.admin_comment || '');
                              }}
                              className="btn btn-ghost btn-xs"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{request.admin_comment}</p>
                      </div>
                    )}

                    {/* Admin comment editor */}
                    {isAdmin && editingComment === request.id && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          className="textarea textarea-bordered w-full text-sm"
                          placeholder="Add an admin response..."
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveComment(request.id)}
                            disabled={savingComment}
                            className="btn btn-primary btn-xs"
                          >
                            {savingComment ? <span className="loading loading-spinner loading-xs"></span> : 'Save'}
                          </button>
                          <button
                            onClick={() => { setEditingComment(null); setCommentText(''); }}
                            className="btn btn-ghost btn-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Add comment button for admins when no comment exists */}
                    {isAdmin && !request.admin_comment && editingComment !== request.id && (
                      <button
                        onClick={() => {
                          setEditingComment(request.id);
                          setCommentText('');
                        }}
                        className="btn btn-ghost btn-xs mt-2 text-base-content/50"
                      >
                        + Add admin response
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => handleUpvote(request.id, request.user_has_upvoted)}
                      className={`btn btn-sm ${
                        request.user_has_upvoted ? 'btn-primary' : 'btn-outline'
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill={request.user_has_upvoted ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <span className="text-lg font-semibold mt-1">{request.upvote_count}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        </div>
      </section>
    </main>
  );
}